import React, { useEffect, useRef } from 'react';
import * as echarts from 'echarts';
import { useTheme } from '../contexts/ThemeContext';

const StackedBarChart = ({ data }) => {
    const chartRef = useRef(null);
    const { theme } = useTheme();
    const isDark = theme === 'dark';
    const axisColor = isDark ? '#ccc' : '#333';

    // Remove invalid entries
    data = data.filter(item => item.channels && item.channels.trim() !== '');

    useEffect(() => {
        if (!data || data.length === 0) return;

        const chart = echarts.init(chartRef.current);

        const channelList = data.map(item => item.channels);

        // Prepare stacked values for each attribution type
        const stackedData = {
            'First Click': [],
            'Last Click': [],
            'Data Attribution 1': [],
            'Data Attribution 2': []
        };

        channelList.forEach(channel => {
            const item = data.find(d => d.channels === channel);
            if (item) {
                stackedData['First Click'].push({
                    name: channel,
                    value: Math.round(item.attribution_first_click_heuristic)
                });
                stackedData['Last Click'].push({
                    name: channel,
                    value: Math.round(item.attribution_last_click_heuristic)
                });
                stackedData['Data Attribution 1'].push({
                    name: channel,
                    value: Math.round(item.attribution_markov_algorithmic)
                });
                stackedData['Data Attribution 2'].push({
                    name: channel,
                    value: Math.round(item.attribution_shapley_size4_conv_rate_algorithmic)
                });
            }
        });

        const series = channelList.map((channel, i) => ({
            name: channel,
            type: 'bar',
            stack: 'total',
            emphasis: { focus: 'series' },
            data: Object.keys(stackedData).map(key =>
                stackedData[key][i] ? stackedData[key][i].value : 0
            )
        }));

        const option = {
            title: {
                text: 'Horizontal Stacked Bar Chart (By Attribution Type)',
                left: 'center',
                top: 10,
                textStyle: { color: axisColor },
            },
            tooltip: {
                trigger: 'axis',
                axisPointer: { type: 'shadow' }
            },
            legend: {
                type: 'scroll',
                top: 50,
                textStyle: { color: axisColor },
            },
            grid: {
                left: '3%',
                right: '4%',
                bottom: '3%',
                containLabel: true
            },
            xAxis: {
                type: 'value',
                axisLabel: {
                    color: axisColor,
                }
            },
            yAxis: {
                type: 'category',
                data: ['First Click', 'Last Click', 'Data Attribution 1', 'Data Attribution 2'],
                axisLabel: {
                    color: axisColor,
                }
            },
            color: [
                '#e6194B', // strong red
                '#3cb44b', // vivid green
                '#ffe119', // bright yellow
                '#4363d8', // deep blue
                '#f58231', // orange
                '#911eb4', // strong purple
                '#46f0f0', // cyan
                '#f032e6', // magenta
                '#bcf60c', // lime
                '#fabebe', // pink
                '#008080', // teal
                '#e6beff', // lavender
                '#9A6324', // brown
                '#fffac8', // light yellow
                '#800000', // maroon
                '#aaffc3', // mint
                '#808000', // olive
                '#ffd8b1', // peach
                '#000075', // navy
                '#808080'  // gray
            ],
            series
        };

        chart.setOption(option);

        const resize = () => chart.resize();
        window.addEventListener('resize', resize);

        return () => {
            chart.dispose();
            window.removeEventListener('resize', resize);
        };
    }, [data]);

    return <div ref={chartRef} style={{ width: '100%', height: '600px' }} />;
};

export default StackedBarChart;
